import React, { useEffect } from 'react';

import WinXP from 'WinXP';
import Login from 'WinXP/apps/Login';
import { AuthProvider } from 'auth/AuthContext';
import userIconSmall from 'assets/windowsIcons/user.png';

/**
 * Parse `?verify=SELECTOR.TOKEN` and `?reset=SELECTOR.TOKEN` from the URL
 * on first mount, then strip them via history.replaceState. When present,
 * dispatch a `lottel:dynamic` event to open the Log On window in the
 * matching sub-mode so the user can complete the flow inline.
 */
function useAuthDeepLinks() {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    let params;
    try {
      params = new URLSearchParams(window.location.search);
    } catch (e) {
      return;
    }
    const verify = params.get('verify');
    const reset = params.get('reset');
    const openApp = params.get('app');
    if (!verify && !reset && !openApp) return;

    // Handle simple `?app=<key>` deep-links (used e.g. by admin notification
    // emails to jump straight to the Admin window).
    if (openApp && !verify && !reset) {
      params.delete('app');
      const search = params.toString();
      window.history.replaceState(
        {},
        '',
        window.location.pathname + (search ? '?' + search : ''),
      );
      const handle = window.setTimeout(() => {
        window.dispatchEvent(
          new CustomEvent('lottel:launch', { detail: openApp }),
        );
      }, 0);
      return () => window.clearTimeout(handle);
    }

    const raw = verify || reset;
    const dotIx = raw.indexOf('.');

    // Strip the query params so a refresh doesn't re-trigger.
    params.delete('verify');
    params.delete('reset');
    const search = params.toString();
    window.history.replaceState(
      {},
      '',
      window.location.pathname + (search ? '?' + search : ''),
    );

    if (dotIx <= 0 || dotIx >= raw.length - 1) return;
    const selector = raw.slice(0, dotIx);
    const token = raw.slice(dotIx + 1);

    const size = { width: 420, height: 340 };
    const injectProps = verify
      ? {
          initialMode: 'verify',
          verifySelector: selector,
          verifyToken: token,
        }
      : {
          initialMode: 'reset',
          resetSelector: selector,
          resetToken: token,
        };
    const detail = {
      header: {
        icon: userIconSmall,
        title: verify ? 'Verify Email' : 'Reset Password',
        buttons: ['close'],
      },
      component: Login,
      defaultSize: size,
      defaultOffset: {
        x: Math.max(20, window.innerWidth / 2 - size.width / 2),
        y: Math.max(20, window.innerHeight / 2 - size.height / 2),
      },
      resizable: false,
      minimized: false,
      maximized: false,
      multiInstance: false,
      injectProps,
    };

    // Defer the dispatch so WinXP's `lottel:dynamic` listener is mounted.
    const handle = window.setTimeout(() => {
      window.dispatchEvent(new CustomEvent('lottel:dynamic', { detail }));
    }, 0);
    return () => window.clearTimeout(handle);
  }, []);
}

const App = () => {
  useAuthDeepLinks();
  return (
    <AuthProvider>
      <WinXP />
    </AuthProvider>
  );
};

export default App;
